import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { PixelSprite } from '@/components/pixel/pixel-sprite'

import {
    ICON_WINDOW_CLOSE,
    ICON_WINDOW_MAX,
    ICON_WINDOW_MIN
} from '@/constants/sprites'

interface RetroWindowProps {
    title: string
    children: ReactNode
    tone?: 'pink' | 'ink'
    className?: string
    bodyClassName?: string
}

const WINDOW_BUTTONS = [ICON_WINDOW_MIN, ICON_WINDOW_MAX, ICON_WINDOW_CLOSE]

/** A chunky old-school desktop window, used as a frame for text and forms. */
const RetroWindow: FC<RetroWindowProps> = ({
    title,
    children,
    tone = 'pink',
    className,
    bodyClassName
}): ReactNode => {
    return (
        <div className={cn('border-2 border-ink bg-white text-ink shadow-pixel-lg', className)}>
            <div
                className={cn(
                    'flex items-center justify-between gap-3 border-b-2 border-ink px-3 py-2',
                    tone === 'pink' ? 'bg-bubblegum text-ink' : 'bg-ink text-paper'
                )}
            >
                <span className='flex min-w-0 items-center gap-2 font-heading text-sm md:text-base'>
                    <span aria-hidden='true' className='size-3 shrink-0 border-2 border-current' />
                    <span className='truncate'>{title}</span>
                </span>

                <span aria-hidden='true' className='flex shrink-0 gap-1.5'>
                    {WINDOW_BUTTONS.map((icon, index) => (
                        <span key={index} className='grid size-6 place-items-center border-2 border-ink bg-white'>
                            <PixelSprite rows={icon} className='size-2.5' />
                        </span>
                    ))}
                </span>
            </div>

            <div className={cn('p-5 md:p-7', bodyClassName)}>
                {children}
            </div>
        </div>
    )
}

export default RetroWindow
